/**
 * 認証関連のAPI実装
 */

import type {
  DeleteAccountResponse,
  MfaSendCodeResponse,
  MfaVerifyRequest,
  MfaVerifyResponse,
} from '@src/api/core';
import { createAuthHeaders } from '@src/api/core/authHeaders';
import { httpClient } from '@src/api/core/http-client';
import { createMockResponse, isMockMode } from '@src/config/api';
import { API_ENDPOINTS } from '@src/constants/apiEndpoints';
import { getRefreshToken } from '@src/modules/security/authHelpers';

/**
 * トークンリフレッシュレスポンス
 */
interface RefreshTokenResponse {
  success: boolean;
  data: {
    accessToken: string;
    expiresIn: number;
  };
}

/**
 * トークンをリフレッシュ
 */
export async function refreshAccessToken(): Promise<RefreshTokenResponse> {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error('リフレッシュトークンが見つかりません');
  }

  const response = await httpClient.post<RefreshTokenResponse>(
    API_ENDPOINTS.AUTH.REFRESH.replace(/^\//, ''),
    {
      refreshToken,
    }
  );

  return response;
}

/**
 * MFA認証コードを送信
 * @param accessToken - アクセストークン（Bearerトークン）
 * @returns MFA送信レスポンス
 */
export async function sendMfaCode(accessToken: string): Promise<MfaSendCodeResponse> {
  const response = await httpClient.post<MfaSendCodeResponse>(
    API_ENDPOINTS.AUTH.MFA_SEND_CODE.replace(/^\//, ''),
    {},
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  return response;
}

/**
 * MFA認証コードを検証
 * @param code - 認証コード
 * @param accessToken - アクセストークン（Bearerトークン）
 * @returns MFA検証レスポンス
 */
export async function verifyMfaCode(code: string, accessToken: string): Promise<MfaVerifyResponse> {
  const payload: MfaVerifyRequest = { code };
  const response = await httpClient.post<MfaVerifyResponse>(
    API_ENDPOINTS.AUTH.MFA_VERIFY.replace(/^\//, ''),
    payload,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  return response;
}

/**
 * MFAバックアップコードを検証
 * @param code - バックアップコード
 * @param accessToken - アクセストークン（Bearerトークン）
 * @returns MFA検証レスポンス
 */
export async function verifyMfaBackupCode(
  code: string,
  accessToken: string
): Promise<MfaVerifyResponse> {
  const payload: MfaVerifyRequest = { code };
  const response = await httpClient.post<MfaVerifyResponse>(
    API_ENDPOINTS.AUTH.MFA_VERIFY_BACKUP.replace(/^\//, ''),
    payload,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  return response;
}

/**
 * アカウントを削除（退会）
 * @returns アカウント削除レスポンス
 * @throws {Error} アクセストークンが見つからない場合
 */
export async function deleteAccount(): Promise<DeleteAccountResponse> {
  // モックモードの場合
  if (isMockMode()) {
    const mockResponse: DeleteAccountResponse = {
      success: true,
      data: {
        message: 'アカウントが正常に削除されました',
      },
      message: 'アカウントが正常に削除されました',
      timestamp: new Date().toISOString(),
    };
    return await createMockResponse<DeleteAccountResponse>(mockResponse, 800);
  }

  // 実際のAPI呼び出し
  const response = await httpClient.delete<DeleteAccountResponse>(
    API_ENDPOINTS.AUTH.DELETE_ACCOUNT.replace(/^\//, ''),
    {
      headers: createAuthHeaders(),
    }
  );

  return response;
}
